import { PyodideSigmaEngine } from './chunk-IAQJY5MS.js';
export { DEFAULT_PIPELINE_PACKAGES, DEFAULT_PYSIGMA_VERSION, DEFAULT_SIGMA_TARGETS, DEFAULT_TARGETS, PyodideSigmaEngine, SIGMA_CONVERTER_PY, backendNameFromPackage, createTargetRegistry } from './chunk-IAQJY5MS.js';

// src/transports/directTransport.ts
var DirectTransport = class {
  engine;
  listeners = /* @__PURE__ */ new Set();
  constructor(options = {}) {
    this.engine = new PyodideSigmaEngine({
      ...options,
      onStatus: (status) => {
        options.onStatus?.(status);
        this.listeners.forEach((l) => l(status));
      }
    });
    void this.engine.init().catch(() => {
    });
  }
  addStatusListener(listener) {
    this.listeners.add(listener);
    listener(this.engine.getStatus());
    return () => this.listeners.delete(listener);
  }
  async getStatus() {
    return this.engine.getStatus();
  }
  installBackend(target) {
    return this.engine.installBackend(target);
  }
  convert(params) {
    return this.engine.convert(params);
  }
  getAvailablePipelines(target) {
    return this.engine.getAvailablePipelines(target);
  }
  dispose() {
    this.listeners.clear();
  }
};

// src/transports/workerTransport.ts
var WorkerTransport = class {
  worker;
  listeners = /* @__PURE__ */ new Set();
  pending = /* @__PURE__ */ new Map();
  nextId = 1;
  lastStatus = {
    ready: false,
    pyodideReady: false,
    installedBackends: []
  };
  constructor(worker) {
    this.worker = worker;
    this.worker.addEventListener("message", this.onMessage);
  }
  onMessage = (event) => {
    const data = event.data;
    if (!data) return;
    if ("type" in data && data.type === "status_update") {
      this.lastStatus = data.status;
      this.listeners.forEach((l) => l(data.status));
      return;
    }
    if ("id" in data) {
      const entry = this.pending.get(data.id);
      if (!entry) return;
      this.pending.delete(data.id);
      if (data.error) entry.reject(new Error(data.error));
      else entry.resolve(data.result);
    }
  };
  request(message) {
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker.postMessage({ ...message, id });
    });
  }
  addStatusListener(listener) {
    this.listeners.add(listener);
    listener(this.lastStatus);
    void this.getStatus().then((status) => listener(status)).catch(() => {
    });
    return () => this.listeners.delete(listener);
  }
  getStatus() {
    return this.request({ type: "status" });
  }
  installBackend(target) {
    return this.request({ type: "install", target });
  }
  convert(params) {
    return this.request({ type: "convert", conversionParams: params });
  }
  getAvailablePipelines(target) {
    return this.request({ type: "get_pipelines", target });
  }
  dispose() {
    this.worker.removeEventListener("message", this.onMessage);
    this.listeners.clear();
    this.pending.clear();
    this.worker.terminate();
  }
};

// src/SigmaConverter.ts
var SigmaConverter = class {
  transport;
  targets;
  cleanupListener;
  readinessListeners = [];
  statusListeners = [];
  currentStatus = {
    ready: false,
    pyodideReady: false,
    installedBackends: []
  };
  constructor(options = {}) {
    this.targets = options.targets ?? null;
    if (options.transport) {
      this.transport = options.transport;
    } else if (options.worker) {
      this.transport = new WorkerTransport(options.worker);
    } else {
      this.transport = new DirectTransport(options);
    }
    this.cleanupListener = this.transport.addStatusListener(
      this.handleStatusUpdate.bind(this)
    );
  }
  handleStatusUpdate(status) {
    this.currentStatus = {
      ready: status?.ready ?? false,
      pyodideReady: status?.pyodideReady ?? false,
      installedBackends: status?.installedBackends ?? [],
      error: status?.error
    };
    this.statusListeners.forEach((l) => l(this.currentStatus));
    this.readinessListeners.forEach((l) => l(this.currentStatus.ready));
  }
  /** Subscribe to status updates. Returns an unsubscribe function. */
  addStatusListener(listener) {
    this.statusListeners.push(listener);
    listener(this.currentStatus);
    return () => {
      const i = this.statusListeners.indexOf(listener);
      if (i !== -1) this.statusListeners.splice(i, 1);
    };
  }
  /** Subscribe to readiness changes. Returns an unsubscribe function. */
  addReadinessListener(listener) {
    this.readinessListeners.push(listener);
    listener(this.currentStatus.ready);
    return () => {
      const i = this.readinessListeners.indexOf(listener);
      if (i !== -1) this.readinessListeners.splice(i, 1);
    };
  }
  /**
   * Convert a Sigma rule to a SIEM query.
   */
  async convert(rule, target, pipeline = [], pipelineYmls = [], filterYml = "", format = "", correlationMethod = "", backendOptions = {}) {
    if (this.targets && !this.targets.has(target)) {
      return { query: "", error: `Unsupported target: ${target}` };
    }
    try {
      if (!this.currentStatus.installedBackends.includes(target)) {
        const install = await this.transport.installBackend(target);
        if (!install.success && install.error) {
          return { query: "", error: `Failed to install backend: ${install.error}` };
        }
      }
      const params = {
        rule,
        target,
        pipelines: pipeline,
        pipelineYmls,
        filterYml,
        format,
        correlationMethod,
        backendOptions
      };
      const plainParams = JSON.parse(JSON.stringify(params));
      const result = await this.transport.convert(plainParams);
      if (result.error) {
        return { query: "", error: result.error };
      }
      return { query: result.result || "" };
    } catch (e) {
      return { query: "", error: e instanceof Error ? e.message : String(e) };
    }
  }
  /** Install a backend ahead of time. */
  installBackend(target) {
    return this.transport.installBackend(target);
  }
  /** List pipelines available for a target. */
  getAvailablePipelines(target) {
    return this.transport.getAvailablePipelines(target);
  }
  /** True once the engine is initialised and idle. */
  isReady() {
    return this.currentStatus.ready;
  }
  /** Current status snapshot. */
  getStatus() {
    return { ...this.currentStatus };
  }
  /** Release listeners and transport resources. */
  dispose() {
    this.cleanupListener();
    this.transport.dispose();
    this.readinessListeners = [];
    this.statusListeners = [];
  }
};

export { DirectTransport, SigmaConverter, WorkerTransport };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map